'use client';

import { useRef, useState } from 'react';
import { LuX } from 'react-icons/lu';
import { GoogleMapsAutocomplete } from '~/features/google/maps';
import { useI18n } from '~/features/i18n';
import type { WidgetLocation } from '../types';
type Props = {
  location: WidgetLocation | null;
  /** Passing `null` clears the location (X button) so the map returns to its overview. */
  onLocationChange: (location: WidgetLocation | null) => void;
  /**
   * The user pressed "Bereken je transportkosten" and has not picked a place yet. Swaps the
   * placeholder for an instruction — on touch the CTA opened the picker with nothing else
   * changing, so it looked like nothing had happened.
   */
  isAwaitingLocation?: boolean;
  className?: string;
};

/**
 * Location picker for the widget: reuses the same Google Places autocomplete the
 * `/teambuildings` filter uses. Picking a place resolves coordinates; the clear
 * (X) button resets the location so the map recenters on the availability overview.
 * The radius selector lives in checkout, not here.
 */
export const LocationInputRow: React.FC<Props> = ({
  location,
  onLocationChange,
  isAwaitingLocation,
  className
}) => {
  const i18n = useI18n();
  const [query, setQuery] = useState(location?.query ?? '');
  // `onChange` (the selected label) fires just before `onResolve` (the coords),
  // so capture the latest label in a ref to attach it to the resolved location.
  const latestQuery = useRef(query);

  // Reflect an externally-set location (e.g. seeded from the /teambuildings presets) in
  // the input via render-time state adjustment (React's "derive state when a prop
  // changes" pattern — no effect). Only fires when the resolved label actually changes,
  // so it never fights the user's in-progress typing.
  const [syncedExternalQuery, setSyncedExternalQuery] = useState(location?.query ?? null);
  if (location?.query != null && location.query !== syncedExternalQuery) {
    setSyncedExternalQuery(location.query);
    setQuery(location.query);
    latestQuery.current = location.query;
  }
  return <div className={className}>
            <div className="flex w-full items-stretch overflow-hidden rounded-full bg-white shadow-lg">
                <GoogleMapsAutocomplete
      // The combobox is a Button variant="outline", so it draws its own outline
      // inside the white pill — a box around the text that stopped short of the
      // clear button and read as a seam between the two. The pill is the control
      // here; its shadow is the only edge it needs.
      className="border-0 py-2.5" value={query}
      // The prompt lives in the placeholder rather than in a line beneath the
      // input: the picker's popover is portalled above everything here, so a
      // separate hint was half-covered by it, and a coloured ring around the
      // pill read as a validation error rather than an instruction.
      placeholder={isAwaitingLocation ? i18n._('pdp.location.input.awaiting', undefined, {
        message: 'Type a town or postcode…'
      }) // prettier-ignore
      : i18n._('pdp.location.input.placeholder', undefined, {
        message: 'Enter your location…'
      }) // prettier-ignore
      } onChange={selected => {
        setQuery(selected);
        latestQuery.current = selected;
      }} onResolve={({
        latitude,
        longitude
      }) => {
        onLocationChange({
          query: latestQuery.current,
          lat: Number(latitude),
          lng: Number(longitude)
        });
      }} />
                {(!!query || !!location) && <button type="button" onClick={() => {
        setQuery('');
        latestQuery.current = '';
        onLocationChange(null);
      }} data-testid="pdp-location-clear" aria-label={i18n._('pdp.location.input.clear', undefined, {
        message: 'Clear location'
      })} className="flex min-h-10 items-center rounded-r-full pl-2 pr-4 text-blue-900/50 transition-colors hover:text-blue-900">
                        <LuX className="size-4" />
                    </button>}
            </div>
        </div>;
};